import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  DeleteSwapPurposeSubtypeResult,
  SwapPurposeSubtype,
  SwapPurposeSubtypeApiItem,
  UpsertSwapPurposeSubtypeRequest,
} from './swap-purpose-subtype.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapPurposeSubtypeService {
  private readonly endpoint = apiUrl('swappurposesubtype');

  constructor(private readonly http: HttpClient) {}

  getSwapPurposeSubtypes(): Observable<SwapPurposeSubtype[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeSwapPurposeSubtype(item))),
    );
  }

  createSwapPurposeSubtype(payload: UpsertSwapPurposeSubtypeRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateSwapPurposeSubtype(id: number, payload: UpsertSwapPurposeSubtypeRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteSwapPurposeSubtype(id: number): Observable<DeleteSwapPurposeSubtypeResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteSwapPurposeSubtypeResult => ({ kind: 'success' })),
      catchError(() => of<DeleteSwapPurposeSubtypeResult>({
        kind: 'error',
        message: 'Unable to delete swap purpose subtype at this time. Please try again.',
      })),
    );
  }

  private normalizeSwapPurposeSubtype(item: SwapPurposeSubtypeApiItem): SwapPurposeSubtype {
    return {
      id: this.toNumber(item.id),
      name: this.toText(item.name),
      purposeId: this.toNullableNumber(item.purposeId),
      purposeName: this.toText(item.purposeName),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: SwapPurposeSubtypeApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): SwapPurposeSubtypeApiItem[] {
    if (Array.isArray(response)) {
      return response as SwapPurposeSubtypeApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as SwapPurposeSubtypeApiItem[];
      }

      if (Array.isArray(items)) {
        return items as SwapPurposeSubtypeApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  private toNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }
}
